/** @jsx isolateComponent */

import { css } from '@emotion/core';
import {
  isolateComponent,
  withModifier,
  labelStyles,
} from '@zapier/style-encapsulation';
import { Icon } from '../../display/Icon';
import type { IconName } from '../../display/Icon';
import { Animation, Colors } from '../../../theme';

export type BooleanInputType = 'radio' | 'checkbox';
export type Props = {
  /**
   * Optional accessibility label for the underlying `input`.
   * Using a `label` tag is generally preferred for more clickable area.
   */
  ariaLabel?: string;

  /**
   * Indicates whether `BooleanInput` is checked.
   */
  checked: boolean;

  /**
   * Indicates whether `BooleanInput` is disabled.
   */
  disabled?: boolean;

  /**
   * `id` that is attached to the underlying `input`, used to attach
   * an external `label` to it via the `for` attribute.
   */
  id?: string;

  /**
   * Indicates whether `BooleanInput` is errored.
   */
  isErrored?: boolean;

  /**
   * Indicates whether the `input` is required.
   * For checkboxes, this means the checkbox itself must be checked.
   * For radios, this means one of the radios with the same `name`
   * must be checked.
   */
  isRequired?: boolean;

  /**
   * `name` that is attached to the underlying `input`, used to group
   * multiple `BooleanInput` values together.
   */
  name?: string;

  /**
   * Handler for when the `input` changes between checked and unchecked.
   */
  onChange: (e: React.SyntheticEvent) => void;

  /**
   * The `tabIndex`. The default is almost always correct unless this
   * `BooleanInput` is being used inside a larger control that manages
   * focus state and keyboard interactions. e.g. a dropdown.
   */
  tabIndex: number;

  /**
   * The type of `input` to render.
   */
  type: BooleanInputType;

  /**
   * `value` that is attached to the underlying `input`, used to differentiate
   * one `input` from another.
   */
  value?: string;
};
const borderWidth = 2;
const Styles = labelStyles('BooleanInput', {
  root: () => [
    css({
      position: 'relative',
      display: 'inline-block',
      // EXPERIMENTAL! NOT A FRONTEND-TEAM APPROVED PATTERN YET!
      // Using CSS variables over JS because they cascade. Using JS,
      // there's no great way to select `focusRing` when the `input`
      // is focused, via `:focus-within` or similar. `focusRing` would
      // have to be selected here via its `class` or a `data-` attribute
      // which isn't great. Alternatively `derivedState` could be used
      // instead of `:focus-within`, but sometimes focus and blur events
      // don't fire correctly, so `:focus-within` is more accurate.
      '--focus-ring-size': 0,
      '--focus-ring-opacity': 0,
      '&:focus-within': {
        '--focus-ring-size': '30px',
        '--focus-ring-opacity': 0.3,
      },
    }),
  ],
  inner: (props: Props) => [
    css({
      display: 'block',
      position: 'relative',
      zIndex: 1,
      border: `${borderWidth}px solid ${Colors.gray80}`,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
    }),
    props.type === 'checkbox' && [
      withModifier(
        'checkbox',
        css({
          width: 18,
          height: 18,
          borderRadius: 3,
          color: Colors.white,
        })
      ),
      props.checked &&
        withModifier(
          'checked',
          css({
            backgroundColor: Colors.ocean,
            borderColor: 'transparent',
          })
        ),
      props.isErrored &&
        withModifier(
          'errored',
          css({
            backgroundColor: props.checked ? Colors.error : 'transparent',
            borderColor: Colors.error,
          })
        ),
      props.disabled &&
        withModifier(
          'disabled',
          css({
            backgroundColor: props.checked ? Colors.gray60 : 'transparent',
            borderColor: Colors.gray60,
            cursor: 'not-allowed',
          })
        ),
    ],
    props.type === 'radio' && [
      withModifier(
        'radio',
        css({
          color: 'transparent',
          width: 20,
          height: 20,
          borderRadius: 20,
        })
      ),
      props.checked &&
        withModifier(
          'checked',
          css({
            borderColor: Colors.blue500,
            color: Colors.blue500,
          })
        ),
      props.isErrored &&
        withModifier(
          'errored',
          css({
            borderColor: Colors.error,
            color: Colors.error,
          })
        ),
      props.disabled &&
        withModifier(
          'disabled',
          css({
            borderColor: Colors.gray60,
            color: Colors.gray60,
            cursor: 'not-allowed',
          })
        ),
    ],
  ],
  layer: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: -borderWidth,
    left: -borderWidth,
    width: `calc(100% + ${2 * borderWidth}px)`,
    height: `calc(100% + ${2 * borderWidth}px)`,
  }),
  // The focus ring always exists, but isn't actually visible until
  // the input is interacted with in order to animate it from one
  // state to another.
  focusRing: (props: Props) => [
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: var(--focus-ring-size);
      height: var(--focus-ring-size);
      opacity: var(--focus-ring-opacity);
      transition: all ${Animation.transitionDuration} ease-in-out;
      pointer-events: none;
      background-color: ${Colors.grey400};
    `,
    withModifier(
      props.type,
      {
        checkbox: css({
          borderRadius: 10,
        }),
        radio: css({
          borderRadius: 'var(--focus-ring-size)',
        }),
      }[props.type]
    ),
    props.checked &&
      withModifier(
        'checked',
        css({
          backgroundColor: Colors.blue500,
        })
      ),
    props.isErrored &&
      withModifier(
        'errored',
        css({
          backgroundColor: Colors.red600,
        })
      ),
  ],
});

/**
 * `BooleanInput` is a stylized `input[type="checkbox | radio"]`.
 */
export const BooleanInput = (props: Props) => {
  return (
    <span css={Styles.root()}>
      <span css={Styles.inner(props)}>
        <input
          aria-label={props.ariaLabel}
          checked={props.checked}
          css={Styles.layer}
          disabled={props.disabled}
          id={props.id}
          name={props.name}
          onChange={props.onChange}
          required={props.isRequired}
          tabIndex={props.tabIndex}
          type={props.type}
          value={props.value}
        />
        {props.checked && (
          <span css={Styles.layer}>
            <Icon
              isBlock={true}
              name={
                {
                  checkbox: 'formCheck' as IconName,
                  radio: 'formRadioDot' as IconName,
                }[props.type]
              }
              size={18}
            />
          </span>
        )}
      </span>
      <span aria-hidden="true" css={Styles.focusRing(props)} />
    </span>
  );
};

BooleanInput.defaultProps = {
  checked: false,
  onChange: () => {},
  tabIndex: 0,
};
